import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAll, ifAny, IGetValueArgs, not } from "@components/smart/FieldInfo";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    PrEmployeeEntity,
    PrEmploymentCloseEntity,
    PrEmploymentEntity,
    PrLaborMarketAccessEntity
} from "@odata/GeneratedEntityTypes";
import {
    CsszEmploymentTypeCode,
    LaborOfficeEmploymentTypeCode,
    PrSalaryComponentTypeCode,
    PrWorkPermitTypeCode
} from "@odata/GeneratedEnums";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import TerminateEmploymentFormView from "@pages/payroll/employment/TerminateEmploymentFormView";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isObjectEmpty } from "@utils/general";
import i18next from "i18next";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, LabelStatus, } from "../../../enums";
import CurrencyType from "../../../types/Currency";
import { getUtcDayjs } from "../../../types/Date";
import { currencyScaleFormatter } from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";

export const TERMINATE_EMPLOYMENT_FORM_ID = "TerminateEmploymentSpecialForm";

export const ProvidePath = BindingContext.localContext("ShouldProvide");

const isNotEndByDeath = (args: IGetValueArgs): boolean => {
    return !args.storage.getValueByPath(createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.IsEndByDeath));
};

const isDataProvided = (args: IGetValueArgs): boolean => {
    return !!args.storage.getValueByPath(ProvidePath);
};

const isServiceType = (args: IGetValueArgs): boolean => {
    const typeCode = args.storage.getValueByPath(createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.LaborOfficeEmploymentTypeCode) as LaborOfficeEmploymentTypeCode);
    return typeCode === LaborOfficeEmploymentTypeCode.ServiceAct234;
};

const isEmploymentType = (args: IGetValueArgs): boolean => {
    const typeCode = args.storage.getValueByPath(createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.LaborOfficeEmploymentTypeCode) as LaborOfficeEmploymentTypeCode);
    return typeCode === LaborOfficeEmploymentTypeCode.Employment;
};

const isDirector = (args: IGetValueArgs): boolean => {
    const typeCode = args.storage.getValueByPath(PrEmploymentEntity.CsszEmploymentTypeCode) as CsszEmploymentTypeCode;
    // PrEmploymentEntity.CsszEmploymentType = M, N, O, P, Q, R, S (M,N not supported in Evala right now, but we keep them noticed here for future use)
    return [
        CsszEmploymentTypeCode.MemberOfAssociation,
        CsszEmploymentTypeCode.Procurator,
        CsszEmploymentTypeCode.MemberOfBoard,
        CsszEmploymentTypeCode.InsuranceAssessor,
        CsszEmploymentTypeCode.Partner_Executive_LimitedPartner_DirectorOfALlc_AndPersonInChargeOfBusinessManagement,

    ].includes(typeCode);
};

const isEarlyTermination = (args: IGetValueArgs): boolean => {
    const laborMarketAccess = args.storage.getValueByPath(createPath(PrEmploymentEntity.Employee, PrEmployeeEntity.LaborMarketAccess));
    if (isObjectEmpty(laborMarketAccess)) {
        return false;
    }
    const permitDateValidTo = laborMarketAccess.DateValidTo;
    const dateEnd = args.storage.getValueByPath(PrEmploymentEntity.DateEnd);
    const workPermitTypeCode = laborMarketAccess.WorkPermitTypeCode as PrWorkPermitTypeCode;
    return [PrWorkPermitTypeCode.EmployeeCard, PrWorkPermitTypeCode.WorkPermit, PrWorkPermitTypeCode.BlueCard].includes(workPermitTypeCode)
        && permitDateValidTo && dateEnd && getUtcDayjs(dateEnd).isBefore(permitDateValidTo, "day");
};

const WITHOUT_CLAIM = "WithoutClaim";

const ServiceTypeSalaryComponentTypes = [
    PrSalaryComponentTypeCode.RetirementBenefit,
    PrSalaryComponentTypeCode.TerminationSettlement
];

const EmploymentTypeSalaryComponentTypes = [
    PrSalaryComponentTypeCode.SeverancePay,
    PrSalaryComponentTypeCode.MedicalLossCompensation
];

export const getDefinition: IGetDefinition = (context: IAppContext): IDefinition => {
    const form: IFormDef = {
        id: TERMINATE_EMPLOYMENT_FORM_ID,
        formControl: TerminateEmploymentFormView,
        translationFiles: getDefinition.translationFiles,
        additionalProperties: [
            { id: PrEmploymentEntity.CsszEmploymentTypeCode },
            { id: PrEmploymentEntity.EmploymentClose },
            { id: createPath(PrEmploymentEntity.Employee, PrEmployeeEntity.LaborMarketAccess, PrLaborMarketAccessEntity.WorkPermitTypeCode) }
        ],
        fieldDefinition: {
            [PrEmploymentEntity.DateEnd]: {
                label: i18next.t("Employment:TerminateEmploymentDialog.DateEnd"),
                affectedFields: [
                    {
                        id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.EmploymentRelationshipTerminationReason),
                        revalidate: true
                    },
                ]
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.Amount)]: {
                label: i18next.t("Employment:TerminateEmploymentDialog.ContributionAmount"),
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath),
                fieldSettings: {
                    unit: CurrencyType.getCurrencyUnit(getCompanyCurrency(context)),
                    formatter: currencyScaleFormatter
                },
            },
            // todo: this field is not in zeplin design, but it is in the data model, so we keep it here for now
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.AverageNetMonthlyIncomeAmount)]: {
                isVisible: ifAll(isDataProvided, isNotEndByDeath),
                fieldSettings: {
                    unit: CurrencyType.getCurrencyUnit(getCompanyCurrency(context)),
                    formatter: currencyScaleFormatter
                },
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.IsEndByDeath)]: {
                label: i18next.t("Employment:TerminateEmploymentDialog.IsEndByDeath"),
                defaultValue: false,
                fieldSettings: {
                    type: SwitchType.YesNo
                }
            },
            [ProvidePath]: {
                type: FieldType.SegmentedButton,
                labelStatus: LabelStatus.Removed,
                defaultValue: true,
                isVisible: isNotEndByDeath,
                fieldSettings: {
                    items: [{
                        id: true,
                        label: i18next.t("Employment:TerminateEmploymentDialog.Provide")
                    }, {
                        id: false,
                        label: i18next.t("Employment:TerminateEmploymentDialog.DontProvide")
                    }]
                }
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.IsSeverancePaidInFull)]: {
                type: FieldType.Switch,
                label: i18next.t("Employment:TerminateEmploymentDialog.IsSeverancePaidInFull"),
                isVisible: ifAll(isDataProvided, isNotEndByDeath, isServiceType, (args) => {
                    const salaryCode = args.storage.getValueByPath(createPath(createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.PrSalaryComponentTypeCode)));
                    return !!salaryCode && ServiceTypeSalaryComponentTypes.includes(salaryCode as PrSalaryComponentTypeCode);
                }),
                fieldSettings: {
                    type: SwitchType.YesNo
                }
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.EmploymentRelationshipTerminationReason)]: {
                type: FieldType.ComboBox,
                label: i18next.t("Employment:TerminateEmploymentDialog.TerminationReason"),
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath, ifAny(isEmploymentType, isDirector)),
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.EmploymentEarlyTerminationReason)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath, isEmploymentType, isEarlyTermination),
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.HasTerminationBenefitEntitled)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath),
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.LaborOfficeEmploymentType)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath, not(isDirector)),
                defaultValue: LaborOfficeEmploymentTypeCode.Employment,
                fieldSettings: {
                    items: [
                        {
                            id: LaborOfficeEmploymentTypeCode.Employment,
                            label: i18next.t("Employment:TerminateEmploymentDialog.Employment")
                        },
                        {
                            id: LaborOfficeEmploymentTypeCode.ServiceAct234,
                            label: i18next.t("Employment:TerminateEmploymentDialog.Service")
                        },
                    ]
                },
                affectedFields: [
                    { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.EmploymentRelationshipTerminationReason) },
                    { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.ServiceRelationshipTerminationReason) },
                    { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.IsSeverancePaidInFull) },
                    { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.PrSalaryComponentType) }
                ]
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.NoUnemploymentSupportType)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.XL,
                isVisible: ifAll(isNotEndByDeath, not(isDataProvided)),
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.PrSalaryComponentType)]: {
                type: FieldType.ComboBox,
                label: i18next.t("Employment:TerminateEmploymentDialog.MonetaryContribution"),
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath),
                fieldSettings: {
                    displayName: "Name",
                    additionalItems: [{
                        id: WITHOUT_CLAIM,
                        label: i18next.t("Employment:TerminateEmploymentDialog.WithoutClaim")
                    }],
                    itemsForRender: (items: ISelectItem[], args: IGetValueArgs) => {
                        const isService = isServiceType(args);
                        return items.filter(i => {
                            const code = i.id as PrSalaryComponentTypeCode;
                            if (isService) {
                                return ServiceTypeSalaryComponentTypes.includes(code);
                            }
                            return EmploymentTypeSalaryComponentTypes.includes(code);
                        });
                    }
                }
            },
            // todo: tohle je kolekce, takze to asi bude nejaky fast entry list, zatim neni nakresleny
            // [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.RetirementInsuranceIntervals)]: {
            //     type: FieldType.MonthRange,
            //     fieldSettings: {
            //         fromPath: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.RetirementInsuranceIntervals, PrRetirementInsuranceIntervalEntity.DateFrom),
            //         toPath: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.RetirementInsuranceIntervals, PrRetirementInsuranceIntervalEntity.DateTo),
            //     },
            //     additionalProperties: [
            //         { id: PrRetirementInsuranceIntervalEntity.DateFrom },
            //         { id: PrRetirementInsuranceIntervalEntity.DateTo },
            //     ],
            // },
            [createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.ServiceRelationshipTerminationReason)]: {
                type: FieldType.ComboBox,
                label: i18next.t("Employment:TerminateEmploymentDialog.TerminationReason"),
                width: BasicInputSizes.L,
                isVisible: ifAll(isDataProvided, isNotEndByDeath, isServiceType),
                fieldSettings: {
                    displayName: "Name"
                }
            },
        },
        groups: [
            {
                id: "Common",
                rows: [
                    [
                        { id: PrEmploymentEntity.DateEnd },
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.IsEndByDeath) },
                    ],
                ]
            },
            {
                id: "DataForLaborOffice",
                title: i18next.t("Employment:TerminateEmploymentDialog.DataForLaborOffice"),
                showGroupDivider: false,
                rows:[
                    [
                        { id: ProvidePath }
                    ], [
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.NoUnemploymentSupportType) },
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.LaborOfficeEmploymentType) },
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.EmploymentRelationshipTerminationReason) },
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.ServiceRelationshipTerminationReason) },
                    ], [
                        // visible if: cizinec s druhem prac. opravneni typu 1,2,4 a datum ukonceni PP je pred datem ukonceni platnosti pracovniho opravneni
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.EmploymentEarlyTerminationReason) },
                    ], [
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.PrSalaryComponentType) },
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.Amount) },
                    ], [
                        { id: createPath(PrEmploymentEntity.EmploymentClose, PrEmploymentCloseEntity.IsSeverancePaidInFull) },
                    ]
                ]
            }
        ]
    };

    return {
        form
    };
};

getDefinition.translationFiles = ["Employment", "Common"];